import { Button } from "@/components/ui/button";
import { Printer, X } from "lucide-react";
import { fmtDateTime } from "@/lib/dates";

export default function PrintableForm({ open, onClose, title, subtitle, children, formCode, signedBy, signedAt, childName }) {
  if (!open) return null;
  const printedAt = fmtDateTime(new Date());

  return (
    <div data-testid="printable-modal" className="fixed inset-0 z-[100] bg-stone-900/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full p-6 flex items-start justify-center">
        <div className="w-full max-w-3xl bg-white rounded-2xl shadow-lift overflow-hidden">
          <div className="print-hide flex items-center justify-between px-6 py-4 border-b border-stone-200" data-print="hide">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Preview · {formCode}</div>
              <div className="font-display text-lg font-medium text-stone-900">{title}</div>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="print-now-btn" onClick={() => window.print()} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
              <Button variant="ghost" onClick={onClose} data-testid="printable-close-btn"><X className="w-5 h-5" /></Button>
            </div>
          </div>

          <div className="printable p-10">
            <div className="flex items-start justify-between border-b-2 border-stone-900 pb-4">
              <div>
                <div className="font-display text-xs uppercase tracking-[0.2em] text-stone-700 font-bold">State of Ohio · Department of Children &amp; Youth</div>
                <h1 className="font-display text-2xl font-semibold text-stone-900 mt-2">{title}</h1>
                {subtitle && <div className="text-sm text-stone-600 mt-1">{subtitle}</div>}
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest font-bold text-stone-700">{formCode}</div>
                {childName && <div className="text-xs text-stone-500 mt-1">Child: <strong>{childName}</strong></div>}
              </div>
            </div>

            <div className="mt-6 space-y-4 text-sm text-stone-900">
              {children}
            </div>

            {(signedBy || signedAt) && (
              <div className="mt-10 pt-6 border-t border-stone-300 text-xs text-stone-700">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="flex-1">
                    <div><strong>Electronically signed by:</strong>{" "}
                      {typeof signedBy === "string" && signedBy.startsWith("data:image")
                        ? "(drawn signature — see below)"
                        : (signedBy || "—")}
                    </div>
                    <div><strong>Date filed:</strong> {signedAt ? fmtDateTime(signedAt) : "—"}</div>
                    <div className="mt-2 text-[10px] text-stone-500">Filed via ChildCheckOh · printed {printedAt}</div>
                  </div>
                  {typeof signedBy === "string" && signedBy.startsWith("data:image") && (
                    <div className="bg-white rounded border border-stone-300 p-2">
                      <img src={signedBy} alt="Drawn signature" className="h-16 w-auto" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export const FormRow = ({ label, value }) => (
  <div className="py-2 border-b border-stone-100 text-left">
    <dt className="text-stone-500 font-semibold uppercase text-[10px] tracking-widest">{label}</dt>
    <dd className="mt-0.5 text-stone-900 whitespace-pre-wrap">{value || <span className="text-stone-400">—</span>}</dd>
  </div>
);
